/**
 * Booking API Service
 *
 * Secure API service for accessing booking data through backend API
 * Pattern A Compliant: Replaces direct Firestore access
 *
 * Usage:
 *   import BookingApiService from './services/BookingApiService';
 *   const appointments = await BookingApiService.getAppointments();
 */

import { authenticatedFetch } from '../utils/apiClient';
import EventBusService from './EventBusService';

const API_URL = process.env.REACT_APP_SECURE_API_URL ||
  'https://us-central1-booker-1d469.cloudfunctions.net/secureApi';

const apiRequest = (endpoint, options = {}) => authenticatedFetch(endpoint, options, API_URL);

/**
 * Booking API Service
 */
const BookingApiService = {
  // ============================================================================
  // APPOINTMENTS
  // ============================================================================

  /**
   * Get all appointments for the current user
   * @param {Object} filters - Optional filters (startDate, endDate)
   */
  async getAppointments(filters = {}) {
    const params = new URLSearchParams();

    // Add filters if provided
    if (filters.startDate) {
      params.append('startDate', filters.startDate.toISOString());
    }
    if (filters.endDate) {
      params.append('endDate', filters.endDate.toISOString());
    }

    const endpoint = `/appointments${params.toString() ? '?' + params.toString() : ''}`;
    const result = await apiRequest(endpoint);
    return result.data;
  },

  /**
   * Get a specific appointment by ID
   */
  async getAppointment(id) {
    const result = await apiRequest(`/appointments/${id}`);
    return result.data;
  },

  /**
   * Get appointments for a specific client by email
   * @param {string} clientEmail - Client's email address
   */
  async getAppointmentsByClient(clientEmail) {
    const encodedEmail = encodeURIComponent(clientEmail);
    const result = await apiRequest(`/appointments/by-client/${encodedEmail}`);
    return result.data;
  },

  /**
   * Get aggregated client data from all appointments
   * Returns summary with booking counts, last/first bookings per client
   */
  async getClientsSummary() {
    const result = await apiRequest('/appointments/clients-summary');
    return result.data;
  },

  /**
   * Create a new appointment
   */
  async createAppointment(data) {
    const result = await apiRequest('/appointments', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    EventBusService.emitEvent('booking.created', {
      appointmentId: result.data?.id,
      clientName: data.clientName,
      date: data.date || data.startTime,
      serviceName: data.serviceName
    }).catch(() => {});
    return result.data;
  },

  /**
   * Update an existing appointment
   */
  async updateAppointment(id, data) {
    const result = await apiRequest(`/appointments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    EventBusService.emitEvent('booking.updated', {
      appointmentId: id,
      changes: Object.keys(data)
    }).catch(() => {});
    return result.data;
  },

  /**
   * Delete an appointment
   */
  async deleteAppointment(id) {
    const result = await apiRequest(`/appointments/${id}`, {
      method: 'DELETE'
    });
    EventBusService.emitEvent('booking.canceled', {
      appointmentId: id
    }).catch(() => {});
    return result.message;
  },

  // ============================================================================
  // BOOKINGS
  // ============================================================================

  /**
   * Get all bookings for the current user
   */
  async getBookings(filters = {}) {
    const params = new URLSearchParams(filters);
    const endpoint = `/bookings${params.toString() ? '?' + params.toString() : ''}`;
    const result = await apiRequest(endpoint);
    return result.data;
  },

  /**
   * Get a specific booking by ID
   */
  async getBooking(id) {
    const result = await apiRequest(`/bookings/${id}`);
    return result.data;
  },

  /**
   * Create a new booking
   *
   * ⚠️ LEGACY API: Use createAppointment() for new code
   *
   * Note: Quota enforcement happens on backend. If you need client-side
   * preflight checks, use useSubscriptionCheck().checkLimit('maxAppointments', count)
   * before calling this method to avoid wasted 403 calls.
   */
  async createBooking(data) {
    const result = await apiRequest('/bookings', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    EventBusService.emitEvent('booking.created', {
      appointmentId: result.data?.id,
      clientName: data.clientName,
      date: data.date || data.startTime,
      serviceName: data.serviceName
    }).catch(() => {});
    return result.data;
  },

  /**
   * Update an existing booking
   */
  async updateBooking(id, data) {
    const result = await apiRequest(`/bookings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    EventBusService.emitEvent('booking.updated', {
      appointmentId: id,
      changes: Object.keys(data)
    }).catch(() => {});
    return result.data;
  },

  /**
   * Delete a booking
   */
  async deleteBooking(id) {
    const result = await apiRequest(`/bookings/${id}`, {
      method: 'DELETE'
    });
    EventBusService.emitEvent('booking.canceled', {
      appointmentId: id
    }).catch(() => {});
    return result.message;
  },

  // ============================================================================
  // SERVICES
  // ============================================================================

  /**
   * Get all services (optionally filtered by clientId)
   */
  async getServices(clientId = null) {
    const params = clientId ? new URLSearchParams({ clientId }) : '';
    const endpoint = `/services${params ? '?' + params.toString() : ''}`;

    // Services endpoint is public, so handle both authenticated and non-authenticated
    try {
      const result = await apiRequest(endpoint);
      return result.data;
    } catch (error) {
      // If not authenticated, try without auth token
      if (error.message.includes('Not authenticated')) {
        const response = await fetch(`${API_URL}${endpoint}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch services: ${response.statusText}`);
        }
        const result = await response.json();
        return result.data;
      }
      throw error;
    }
  },

  /**
   * Get a specific service by ID
   */
  async getService(id) {
    const result = await apiRequest(`/services/${id}`);
    return result.data;
  },

  /**
   * Create a new service (requires authentication)
   */
  async createService(data) {
    const result = await apiRequest('/services', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return result.data;
  },

  /**
   * Update an existing service (requires authentication)
   */
  async updateService(id, data) {
    const result = await apiRequest(`/services/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return result.data;
  },

  /**
   * Delete a service (requires authentication)
   */
  async deleteService(id) {
    const result = await apiRequest(`/services/${id}`, {
      method: 'DELETE'
    });
    return result.message;
  },

  // ============================================================================
  // AVAILABILITY
  // ============================================================================

  /**
   * Get available time slots for a service on a specific date
   * @param {string} clientId - The client ID
   * @param {string} serviceId - The service ID
   * @param {string} date - Date in YYYY-MM-DD format
   * @param {number} duration - Service duration in minutes
   */
  async getAvailability(clientId, serviceId, date, duration = 60) {
    const params = new URLSearchParams({
      date,
      serviceId,
      duration: duration.toString()
    });

    // Use the secure API availability endpoint
    const response = await fetch(`${API_URL}/appointments/availability/${clientId}?${params.toString()}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch availability: ${response.statusText}`);
    }

    const result = await response.json();
    return result;
  },

  /**
   * Update user availability settings
   * @param {Object} availability - Availability settings (days and hours)
   */
  async updateAvailability(availability) {
    const result = await apiRequest('/user/availability', {
      method: 'PUT',
      body: JSON.stringify({ availability })
    });
    return result.data;
  },

  // ============================================================================
  // INTEGRATIONS
  // ============================================================================

  /**
   * Get integrations (secrets redacted)
   */
  async getIntegrations() {
    const result = await apiRequest('/integrations');
    return result.data;
  },

  /**
   * Update integrations (server-side validation & encryption)
   * @param {Object} integrations - Integration settings
   */
  async updateIntegrations(integrations) {
    const result = await apiRequest('/integrations', {
      method: 'PUT',
      body: JSON.stringify(integrations)
    });
    return result.data;
  },

  // ============================================================================
  // HEALTH CHECK
  // ============================================================================

  /**
   * Check API health
   */
  async checkHealth() {
    const response = await fetch(`${API_URL}/health`);
    return await response.json();
  },

  // ============================================================================
  // WAITLIST
  // ============================================================================

  /**
   * Get all waitlist entries
   * @param {string} status - Filter by status ('all', 'waiting', 'booked', etc.)
   */
  async getWaitlistEntries(status = 'all') {
    const params = status !== 'all' ? `?status=${status}` : '';
    const result = await apiRequest(`/waitlist${params}`);
    return result.waitlistEntries || result.data || [];
  },

  /**
   * Get waitlist entries for specific client
   * @param {string} clientEmail - Client's email address
   */
  async getClientWaitlistEntries(clientEmail) {
    const result = await apiRequest(`/waitlist/client/${encodeURIComponent(clientEmail)}`);
    return result.data;
  },

  /**
   * Add to waitlist
   * @param {Object} data - Waitlist entry data
   */
  async addToWaitlist(data) {
    const result = await apiRequest('/waitlist', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return result;
  },

  /**
   * Remove from waitlist
   * @param {string} waitlistId - Waitlist entry ID
   */
  async removeFromWaitlist(waitlistId) {
    const result = await apiRequest(`/waitlist/${waitlistId}`, {
      method: 'DELETE'
    });
    return result;
  },

  /**
   * Notify waitlist customer
   * @param {string} waitlistId - Waitlist entry ID
   * @param {string} message - Custom message (optional)
   */
  async notifyWaitlistCustomer(waitlistId, message = '') {
    const result = await apiRequest(`/waitlist/${waitlistId}/notify`, {
      method: 'POST',
      body: JSON.stringify({ message })
    });
    return result;
  },

  /**
   * Book from waitlist (convert to appointment)
   * @param {string} waitlistId - Waitlist entry ID
   * @param {Object} appointmentData - Appointment details
   */
  async bookFromWaitlist(waitlistId, appointmentData) {
    const result = await apiRequest(`/waitlist/${waitlistId}/book`, {
      method: 'POST',
      body: JSON.stringify({ appointmentData })
    });
    return result;
  },

  // ============================================================================
  // RECURRING BOOKINGS
  // ============================================================================

  /**
   * Get all recurring bookings
   */
  async getRecurringBookings() {
    const result = await apiRequest('/recurring-bookings');
    return result.data;
  },

  /**
   * Get specific recurring booking
   * @param {string} bookingId - Recurring booking ID
   */
  async getRecurringBooking(bookingId) {
    const result = await apiRequest(`/recurring-bookings/${bookingId}`);
    return result.data;
  },

  /**
   * Create recurring booking series
   * @param {Object} data - Recurring booking data
   */
  async createRecurringBooking(data) {
    const result = await apiRequest('/recurring-bookings', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return result;
  },

  /**
   * Update recurring booking
   * @param {string} bookingId - Recurring booking ID
   * @param {Object} updates - Fields to update
   */
  async updateRecurringBooking(bookingId, updates) {
    const result = await apiRequest(`/recurring-bookings/${bookingId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
    return result;
  },

  /**
   * Cancel/Delete recurring booking
   * @param {string} bookingId - Recurring booking ID
   * @param {boolean} cancelFutureOnly - If true, only cancel future appointments
   */
  async cancelRecurringBooking(bookingId, cancelFutureOnly = true) {
    const params = cancelFutureOnly ? '?cancelFutureOnly=true' : '';
    const result = await apiRequest(`/recurring-bookings/${bookingId}${params}`, {
      method: 'DELETE'
    });
    return result;
  },

  // ============================================================================
  // AI CALLER SMS INTEGRATION (Alternative to local Twilio)
  // ============================================================================

  /**
   * Check if user has Twilio configured in AI Caller
   * @returns {Promise<{configured: boolean, twilioNumber?: string, setupUrl?: string}>}
   */
  async checkAICallerTwilioConfig() {
    const result = await apiRequest('/ai-caller-sms/check-config');
    return result;
  },

  /**
   * Send SMS via AI Caller Internal API
   * Uses customer's own Twilio account (configured in AI Caller)
   * @param {string} to - Recipient phone number (E.164 format)
   * @param {string} message - SMS message content
   * @returns {Promise<{success: boolean, messageSid?: string, error?: string}>}
   */
  async sendAICallerSMS(to, message) {
    const result = await apiRequest('/ai-caller-sms/send', {
      method: 'POST',
      body: JSON.stringify({ to, message })
    });
    return result;
  },

  /**
   * Send bulk SMS via AI Caller Internal API
   * @param {string[]} recipients - Array of recipient phone numbers
   * @param {string} message - SMS message content
   * @returns {Promise<{success: boolean, sent: Array, failed: Array, total: number}>}
   */
  async sendAICallerBulkSMS(recipients, message) {
    const result = await apiRequest('/ai-caller-sms/bulk', {
      method: 'POST',
      body: JSON.stringify({ recipients, message })
    });
    return result;
  },

  /**
   * Send booking reminder SMS via AI Caller
   * @param {string} appointmentId - Appointment ID
   * @returns {Promise<{success: boolean, messageSid?: string, error?: string}>}
   */
  async sendAICallerBookingReminder(appointmentId) {
    const result = await apiRequest('/ai-caller-sms/reminder', {
      method: 'POST',
      body: JSON.stringify({ appointmentId })
    });
    return result;
  }
};

export default BookingApiService;
